import { useMemo, useRef } from 'preact/hooks';
import { useBlockStore } from '../stores/BlockStore';
import { useConfigStore } from '../stores/ConfigStore';
import { honeyBotId, honeyBotTypeName } from '@/constants';

function getErrorMessage({ reason, text = {}, block = {} }) {
	if (text.errors?.[reason]) {
		return text.errors[reason];
	}
	if (text.defaultErrors?.[reason]) {
		return text.defaultErrors[reason];
	}
	if (text.errors?.required) {
		return text.errors.required;
	}
	if (text.defaultErrors?.required) {
		return text.defaultErrors.required;
	}

	return 'This field is required';
}

export default function useBlock({ id, typeName, typeOf = 0 }) {
	const blockState = useBlockStore();
	const { blocks = {}, text = {}, blockRules = {}, layout } = useConfigStore();
	const errorMessagesRef = useRef({});

	const block = useMemo(() => {
		if (id === 'recaptcha') {
			return {
				id: 'recaptcha',
				typeName: 'recaptcha',
				properties: {}
			};
		}

		if (id === honeyBotId) {
			return {
				id: honeyBotId,
				typeName: honeyBotTypeName,
				title: 'What is your opinion?',
				properties: {}
			};
		}

		if (id) {
			return blocks[id] ?? {};
		}

		if (typeName) {
			const idFromTypeName = layout?.filter(id => blocks[id]?.typeName === typeName)[
				typeOf
			];

			return blocks[idFromTypeName] ?? {};
		}

		return {};
	}, [blocks, id, typeName, typeOf, layout]);

	const state = useMemo(() => {
		return blockState[block.id] ?? {};
	}, [block?.id, blockState]);

	const actions = useMemo(() => {
		return {
			setValue: value =>
				blockState.actions.dispatch({
					type: 'set_value',
					payload: {
						value,
						block,
						rules: blockRules[block.id],
						layout
					}
				}),
			setNestedValue: (datafieldId, value) =>
				blockState.actions.dispatch({
					type: 'set_nested_value',
					payload: {
						value,
						block,
						datafieldId,
						rules: blockRules[block.id],
						layout
					}
				}),
			setHoneyBotValue: value => {
				blockState.actions.dispatch({
					type: 'set_honeybot_value',
					payload: {
						value
					}
				});
			},
			skipQuestion: () =>
				blockState.actions.dispatch({
					type: 'set_skip',
					payload: {
						block,
						blocks,
						layout,
						blockRules
					}
				})
		};
	}, [layout, blockState.actions, blockRules, block, blocks]);

	const errorMessage = state.showError
		? getErrorMessage({
				reason: state.errorReason,
				text,
				block
		  })
		: '';

	const errorMessages = useMemo(() => {
		if (block.typeName !== 'contact') {
			return errorMessagesRef.current;
		}

		return Object.keys(state).reduce((dataFields, currentPropKey) => {
			if (!isNaN(Number(currentPropKey))) {
				dataFields[currentPropKey] = getErrorMessage({
					reason: state[currentPropKey].errorReason,
					text
				});
			}
			return dataFields;
		}, {});
	}, [block.typeName, state, text]);

	// const errorMessages = errorOnChildrenBlocks.indexOf(block.typeName) > -1
	//   ? Object
	//   : {};

	return {
		block,
		state,
		errorMessage,
		errorMessages,
		actions
	};
}
